import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LicenseCard } from "@/components/LicenseCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileKey } from "lucide-react";

export default function Licenses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/licenses")
      .then(({ data }) => setLicenses(data))
      .catch((e) => toast.error(errMsg(e, "Could not load licences")))
      .finally(() => setLoading(false));
  }, []);

  const filtered = licenses.filter((l) => (tab === "all" ? true : l.status === tab));
  const mySide = (l) => (l.client_user_id === user?.id ? "client" : "model");

  return (
    <div className="page-container max-w-4xl py-8 sm:py-12">
      <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Licences</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Time-bound usage rights — every licence carries the ethical agreement and the model's consent terms.
      </p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-xl">
          <TabsTrigger data-testid="licenses-tab-all" value="all" className="rounded-lg">All</TabsTrigger>
          <TabsTrigger data-testid="licenses-tab-active" value="active" className="rounded-lg">Active</TabsTrigger>
          <TabsTrigger data-testid="licenses-tab-expired" value="expired" className="rounded-lg">Expired</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="mt-6 space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed p-12 text-center">
          <FileKey className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-display text-xl">No licences {tab !== "all" ? `(${tab})` : "yet"}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.role === "client"
              ? "Book a model and complete payment to receive your first licence."
              : "When a client pays for your look, the licence appears here."}
          </p>
          {user?.role === "client" && (
            <Button className="mt-4 rounded-xl" onClick={() => navigate("/explore")}>Explore models</Button>
          )}
        </div>
      ) : (
        <div data-testid="licenses-list" className="mt-6 space-y-4">
          {filtered.map((l) => (
            <LicenseCard key={l.id} license={l} mySide={mySide(l)} />
          ))}
        </div>
      )}
    </div>
  );
}
